import { useCallback, useEffect, useMemo, useState } from "react"

import { loadStripe } from "@stripe/stripe-js"
import { Elements } from "@stripe/react-stripe-js"
import PropTypes from "prop-types"

import styles from "./stripe_js_container.module.scss"

import museoSans500 from "~/fonts/museo_sans_rounded-500.woff2"
import museoSans700 from "~/fonts/museo_sans_rounded-700.woff2"
import museoSans900 from "~/fonts/museo_sans_rounded-900.woff2"
import museoSans500Inline from "~/fonts/museo_sans_rounded-500.woff2?inline"
import museoSans700Inline from "~/fonts/museo_sans_rounded-700.woff2?inline"
import museoSans900Inline from "~/fonts/museo_sans_rounded-900.woff2?inline"

import { logCustomDatadogAction } from "app/scripts/shared_setup"

// The cross-origin Stripe iframe can't see the app's @font-face, so it needs its own `fonts` sources.
// Dev inlines them because Chrome's Local Network Access blocks the iframe from fetching loopback Vite.
const absoluteFontUrl = (asset) => new URL(asset, window.location.origin).href
const STRIPE_FONTS = import.meta.env.DEV
  ? [
      { family: "Museo Sans Rounded", src: `url(${museoSans500Inline}) format("woff2")`, weight: "500" },
      { family: "Museo Sans Rounded", src: `url(${museoSans700Inline}) format("woff2")`, weight: "700" },
      { family: "Museo Sans Rounded", src: `url(${museoSans900Inline}) format("woff2")`, weight: "900" },
    ]
  : [
      { family: "Museo Sans Rounded", src: `url(${absoluteFontUrl(museoSans500)}) format("woff2")`, weight: "500" },
      { family: "Museo Sans Rounded", src: `url(${absoluteFontUrl(museoSans700)}) format("woff2")`, weight: "700" },
      { family: "Museo Sans Rounded", src: `url(${absoluteFontUrl(museoSans900)}) format("woff2")`, weight: "900" },
    ]

// Per Stripe's guidance, `loadStripe` must run once at module scope rather than inside the
// component's render so a fresh promise is not allocated on every re-render. The developer-tools
// flag is keyed by email, so we cache one promise per (email, key) combination.
//
// https://docs.stripe.com/stripe-js/react#stripe-elements-provider
const stripePromisesByKey = new Map()

// Test-only: module-scoped promise cache survives across Vitest cases.
export const clearStripePromiseCacheForTests = () => stripePromisesByKey.clear()

const buildStripeCacheKey = (email) => {
  const isIftttEmail = email && email.endsWith("@ifttt.com")
  const enableDeveloperTools =
    (window.App?.env === "development" || window.location.hostname === "web-rehearsal.ifttt.com") &&
    isIftttEmail
  const publishableKey = window.App?.stripePublishableKey || ""
  return `${publishableKey}|${enableDeveloperTools ? "dev" : "prod"}`
}

const loadStripeForCacheKey = (email, cacheKey) => {
  let promise = stripePromisesByKey.get(cacheKey)
  if (!promise) {
    const isIftttEmail = email && email.endsWith("@ifttt.com")
    const enableDeveloperTools =
      (window.App?.env === "development" || window.location.hostname === "web-rehearsal.ifttt.com") &&
      isIftttEmail
    const publishableKey = window.App?.stripePublishableKey || ""

    promise = loadStripe(publishableKey, {
      developerTools: {
        assistant: {
          enabled: enableDeveloperTools,
        },
      },
    }).catch((error) => {
      stripePromisesByKey.delete(cacheKey)
      throw error
    })
    stripePromisesByKey.set(cacheKey, promise)
  }
  return promise
}

const stripeLoadErrorMessage = (error) => {
  const message = (error?.message || "").toLowerCase()
  if (message.includes("failed to load") || message.includes("network")) {
    return "We couldn't load the secure payment form. Disable ad blockers or privacy extensions for this site, or try another browser."
  }
  return "We couldn't load the secure payment form. Please retry."
}

// Fixed tab label size — Stripe appearance has no media queries; resizing the viewport used to
// rebuild `options` and remount Elements (WEB-CLIENT-RJM / RVV).
const TAB_LABEL_SIZE = "16px"

export default function StripeJsContainer({
  children,
  email,
  paymentElementLayout = "tabs",
  restrictToCardOnly = false,
  selectedPrice,
  selectedCurrency,
  isBillingUpdateFlow = false,
}) {
  const isIftttEmail = email && email.endsWith("@ifttt.com")
  const cacheKey = useMemo(() => buildStripeCacheKey(email), [email])
  const [stripeLoadState, setStripeLoadState] = useState({ status: "loading", promise: null })
  const [retryToken, setRetryToken] = useState(0)

  const loadStripeInstance = useCallback(() => {
    setStripeLoadState({ status: "loading", promise: null })

    loadStripeForCacheKey(email, cacheKey)
      .then((stripe) => {
        const resolvedPromise = Promise.resolve(stripe)
        stripePromisesByKey.set(cacheKey, resolvedPromise)
        setStripeLoadState({ status: "ready", promise: resolvedPromise })
      })
      .catch((error) => {
        logCustomDatadogAction("stripe_load_failed", {
          error_message: error?.message || String(error),
          error_name: error?.name,
          publishable_key_present: Boolean(window.App?.stripePublishableKey),
        })
        setStripeLoadState({ status: "failed", promise: null, error })
      })
  }, [cacheKey, email])

  useEffect(() => {
    if (retryToken > 0) {
      stripePromisesByKey.delete(cacheKey)
    }
    loadStripeInstance()
  }, [cacheKey, loadStripeInstance, retryToken])

  const handleRetryStripeLoad = () => {
    stripePromisesByKey.delete(cacheKey)
    setRetryToken(token => token + 1)
  }

  const currency = selectedCurrency || "usd"

  let paymentMethodTypes = ["card"]
  if (restrictToCardOnly && !isBillingUpdateFlow) {
    paymentMethodTypes = ["card"]
  } else if (window.App.paymentStrategy === "admin_test" && isIftttEmail) {
    paymentMethodTypes = ["card", "us_bank_account"]
  } else if (window.App.paymentStrategy === "stripe_dashboard") {
    paymentMethodTypes = []
  }

  // Same border width as `.Input` / `.Input:focus` so invalid state doesn’t shift layout (`_colors.scss` red).
  const errorColor = "#d0011b"
  // #111 = $primary-eerie-black (Text/Primary); one const so a future brand tweak is one edit.
  const primaryText = "#111111"
  const inputBorderInvalid = `2px solid ${errorColor}`
  // Match My Applets filter tabs (`my_applets.module.scss`: $tertiary-text-color / $primary-text-color).
  const tabTextPrimary = primaryText
  const tabTextTertiary = "#999999"
  const tabUnderlineInactive = "4px solid transparent"
  // Match `.Input` horizontal inset (16px, minus Stripe's tab inset) so tab labels line up with field text.
  const tabPadding = "2px 14px"
  const tabLineHeight = "1.2"
  const tabLabelPaddingTop = "4px"
  const tabRowSpacingBelow = "0"
  const accordionItemBorder = `4px solid ${primaryText}1a`
  const accordionItemBorderSelected = `4px solid ${primaryText}4d`
  const gridColumnSpacingDefault = "4px"
  // Accordion headers: halve icon↔title gap (~gridColumnSpacing + TabIcon inset).
  const gridColumnSpacingAccordion = "2px"
  const accordionTabIconPaddingRight = "4px"

  const isAccordionLayout =
    paymentElementLayout === "accordion_radios" || paymentElementLayout === "accordion_no_radios"

  const elementOptions = useMemo(() => {
    const modeOptions = isBillingUpdateFlow
      ? { mode: "setup", currency }
      : {
          mode: "subscription",
          amount: selectedPrice,
          currency,
          setupFutureUsage: "off_session",
        }

    return {
      ...modeOptions,
      paymentMethodTypes: paymentMethodTypes,
      fonts: STRIPE_FONTS,
      appearance: {
      // https://docs.stripe.com/elements/appearance-api?api-integration=paymentintents
      theme: "flat",
      labels: "floating",
      // `condensed` groups related card fields (expiry + CVC) so Stripe can keep them on one row where width allows.
      inputs: "condensed",
      variables: {
        // https://docs.stripe.com/elements/appearance-api?api-integration=paymentintents#variables
        fontFamily: '"Museo Sans Rounded", "Avenir Next", "Avenir", helvetica, arial, sans-serif',
        fontSizeBase: "16px",
        fontLineHeight: "1.1",
        fontWeightBold: "700",
        borderRadius: "8px",
        // Minimum horizontal slack so card row + expiry/CVC row fit in narrow checkout columns (~mid-width grid); Stripe stacks when below ~400px iframe width.
        gridColumnSpacing: isAccordionLayout ? gridColumnSpacingAccordion : gridColumnSpacingDefault,
        gridRowSpacing: "10px",
        tabSpacing: "4px",
        colorPrimary: primaryText,
        colorPrimaryText: primaryText,
        colorText: primaryText,
        colorTextSecondary: "#77787D",
        // Match Not now / `$tertiary-text-color` (`#999`) — lighter placeholder than body text.
        colorTextPlaceholder: tabTextTertiary,
        colorDanger: errorColor,
        colorBackground: "#ffffff",
        colorIconTabSelected: tabTextPrimary,
        iconColor: tabTextPrimary,
        tabIconColor: tabTextTertiary,
        tabIconHoverColor: tabTextPrimary,
        tabIconSelectedColor: tabTextPrimary,
        iconCardErrorColor: errorColor,
        iconCardCvcErrorColor: errorColor,
        focusBoxShadow: "none",
        focusOutline: "none",
      },
      rules: {
        // https://docs.stripe.com/elements/appearance-api?api-integration=paymentintents#supported-css-properties
        ".Tab": {
          // Match `.Input` horizontal rhythm; My Applets–style tab treatment (tertiary / primary + underline).
          padding: tabPadding,
          marginBottom: tabRowSpacingBelow,
          fontSize: TAB_LABEL_SIZE,
          lineHeight: tabLineHeight,
          border: "none",
          borderBottom: tabUnderlineInactive,
          borderRadius: "0",
          backgroundColor: "transparent",
          color: tabTextTertiary,
          boxShadow: "none",
        },
        ".Tab:hover": {
          color: tabTextPrimary,
        },
        ".Tab--selected": {
          backgroundColor: "transparent",
          border: "none",
          borderBottom: tabUnderlineInactive,
          color: tabTextPrimary,
          boxShadow: "none",
        },
        ".Tab--selected:hover": {
          color: tabTextPrimary,
          borderBottom: tabUnderlineInactive,
        },
        ".Tab:focus": {
          color: tabTextPrimary,
          borderBottom: tabUnderlineInactive,
          boxShadow: "none",
          outline: "none",
        },
        ".Tab:focus-visible": {
          color: tabTextPrimary,
          borderBottom: tabUnderlineInactive,
          boxShadow: "none",
          outline: "none",
        },
        ".Tab--selected:focus": {
          borderBottom: tabUnderlineInactive,
          boxShadow: "none",
          outline: "none",
        },
        ".Tab--selected:focus-visible": {
          borderBottom: tabUnderlineInactive,
          boxShadow: "none",
          outline: "none",
        },
        ".TabLabel": {
          fontSize: TAB_LABEL_SIZE,
          fontWeight: "700",
          lineHeight: tabLineHeight,
          // Stripe defaults add horizontal inset; keep top alignment only and trim right padding.
          padding: `${tabLabelPaddingTop} 0 0 0`,
          paddingRight: "0",
          paddingLeft: "0",
        },
        ".Input": {
          fontSize: "1.125em",
          fontWeight: "700", // Same from labels resting
          border: `2px solid ${primaryText}1a`,
          borderRadius: "16px",
          backgroundColor: "#ffffff",
          padding: "10px 16px",
        },
        ".Input:focus": {
          border: `2px solid ${primaryText}`,
          boxShadow: "none",
        },
        ".Input--invalid": {
          border: inputBorderInvalid,
          boxShadow: "none",
        },
        ".Input--invalid:focus": {
          border: inputBorderInvalid,
          boxShadow: "none",
          outline: "none",
        },
        ".Input--invalid:hover": {
          border: inputBorderInvalid,
          boxShadow: "none",
        },
        ".Label--invalid": {
          color: errorColor,
        },
        ".Error": {
          color: errorColor,
          fontSize: "1.125em",
          fontWeight: "700",
          lineHeight: "1.5",
          // Near `_form_base.scss` `.field .note` (0.5em top, 0.2em left).
          margin: "0.5em 0 0 0.2em",
        },
        ".Label--resting": {
          fontSize: "1.125em",
          fontWeight: "700",
          color: `${primaryText}99`,
        },
        ".Label--floating": {
          // Larger + higher contrast than Stripe default so “Card number” etc. stay readable when floated.
          fontSize: "14px",
          fontWeight: "700",
          color: primaryText,
          lineHeight: "1.25",
        },
        // US bank institution list — align with `_form_base.scss` labels / list tone (Avenir + 700 + body scale).
        ".PickerItem": {
          padding: "10px 16px",
          fontSize: "1.125em",
          fontWeight: "700",
          color: primaryText,
          lineHeight: "1.45",
        },
        ".PickerItem--selected": {
          fontSize: "1.125em",
          fontWeight: "700",
          color: primaryText,
        },
        ".PickerItem--highlight": {
          fontSize: "1.125em",
          fontWeight: "700",
          color: primaryText,
        },
        ".PickerItem--disabled": {
          fontSize: "1.125em",
          fontWeight: "700",
          color: `${primaryText}66`,
        },
        ".PickerItem--new": {
          fontSize: "1.125em",
          fontWeight: "700",
          color: primaryText,
        },
        ".PickerItem:hover": {
          color: primaryText,
        },
        ".PickerItem:focus": {
          color: primaryText,
        },
        // “Enter bank details manually” / similar — `_links.scss` `.link-inline` + `.link-standalone`.
        ".Link": {
          fontSize: "1.125em",
          fontWeight: "700",
          lineHeight: "1.45",
          color: primaryText,
          textDecoration: "underline",
        },
        ".Link:hover": {
          color: primaryText,
          textDecoration: "underline",
        },
        ".Link:focus": {
          color: primaryText,
          textDecoration: "underline",
          outline: "none",
        },
        ".SecondaryLink": {
          fontSize: "1.125em",
          fontWeight: "700",
          lineHeight: "1.45",
          color: "#0099ff",
          textDecoration: "none",
        },
        ".SecondaryLink:hover": {
          color: "#0099ff",
          textDecoration: "none",
        },
        ".SecondaryLink:focus": {
          color: "#0099ff",
          textDecoration: "none",
          outline: "none",
        },
        // Supporting copy under bank search (matches `colorTextSecondary` / `$secondary-text-color` tone).
        ".Text": {
          fontSize: "1.125em",
          fontWeight: "600",
          lineHeight: "1.5",
          color: "#666666",
        },
        ".TermsText": {
          fontSize: "1.125em",
          fontWeight: "600",
          lineHeight: "1.5",
          color: "#666666",
        },
        ".TermsLink": {
          fontSize: "1.125em",
          fontWeight: "700",
          lineHeight: "1.45",
          color: "#0099ff",
          textDecoration: "none",
        },
        ".TermsLink:hover": {
          color: "#0099ff",
          textDecoration: "none",
        },
        ".TermsLink:focus": {
          color: "#0099ff",
          outline: "none",
        },
        ".PickerAction": {
          fontSize: "1.125em",
          fontWeight: "700",
          lineHeight: "1.45",
          color: primaryText,
          textDecoration: "underline",
        },
        ".PickerAction:hover": {
          color: primaryText,
        },
        ".PickerAction:focus": {
          color: primaryText,
          outline: "none",
        },
        ".AccordionItem": {
          border: accordionItemBorder,
          borderRadius: "8px",
          marginBottom: "8px",
          boxShadow: "none",
          fontSize: TAB_LABEL_SIZE,
          fontWeight: "700",
          lineHeight: tabLineHeight,
          color: tabTextTertiary,
        },
        ".AccordionItem:hover": {
          border: accordionItemBorder,
          boxShadow: "none",
          color: tabTextPrimary,
        },
        ".AccordionItem:focus": {
          border: accordionItemBorder,
          boxShadow: "none",
          outline: "none",
        },
        ".AccordionItem--selected": {
          border: accordionItemBorderSelected,
          boxShadow: "none",
          color: tabTextPrimary,
        },
        ".AccordionItem--selected:hover": {
          border: accordionItemBorderSelected,
          boxShadow: "none",
          color: tabTextPrimary,
        },
        ".AccordionItem--selected:focus": {
          border: accordionItemBorderSelected,
          boxShadow: "none",
          outline: "none",
        },
        ...(isAccordionLayout
          ? {
              ".TabIcon": {
                paddingRight: accordionTabIconPaddingRight,
              },
            }
          : {}),
      },
    },
    }
  }, [selectedPrice, currency, paymentMethodTypes.join(","), isBillingUpdateFlow, paymentElementLayout])

  // Key on mode + layout only — not selectedPrice or currency. Both amount and currency are updatable
  // options, so they flow through `options` and update the Elements session in place. This keeps the
  // annual/monthly toggle and the currency switch from remounting Express Checkout, refetching setup
  // intents, or reprobing wallets.
  const elementsKey = `${isBillingUpdateFlow ? "setup" : "subscription"}-${paymentElementLayout}`

  if (stripeLoadState.status === "failed") {
    return (
      <div className={styles["stripe-load-error"]} aria-live="polite">
        <p>{stripeLoadErrorMessage(stripeLoadState.error)}</p>
        <button type="button" className="button-secondary" onClick={handleRetryStripeLoad}>
          Retry loading payment form
        </button>
      </div>
    )
  }

  if (stripeLoadState.status !== "ready" || !stripeLoadState.promise) {
    return (
      <div className={styles["stripe-load-placeholder"]} aria-busy="true" aria-live="polite">
        Loading secure payment form...
      </div>
    )
  }

  // Usage of 'stripe' and 'elements' must be in a child component of <Elements>
  return (
    <Elements key={elementsKey} stripe={stripeLoadState.promise} options={elementOptions}>
      {children}
    </Elements>
  )
}

StripeJsContainer.propTypes = {
  children: PropTypes.node.isRequired,
  email: PropTypes.string,
  isBillingUpdateFlow: PropTypes.bool,
  paymentElementLayout: PropTypes.oneOf(["tabs", "accordion_radios", "accordion_no_radios"]),
  restrictToCardOnly: PropTypes.bool,
  selectedPrice: PropTypes.number,
  selectedCurrency: PropTypes.string,
}
